import Cookie from 'js-cookie';
import axios from 'utils/axios';
import { TOKEN_KEY, SESSION_INFO_KEY, BEARER_PREFIX } from './AuthConstants';

/**
 * Clase creada con el propósito de centralizar la logica para el
 * manejo de los items relacionados con la sesion.
 */
class AuthService {
  setAxiosInterceptors = ({ onLogout }) => {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          this.storeSessionData(null);

          if (onLogout) {
            onLogout();
          }
        }

        return Promise.reject(error);
      },
    );
  };

  handleAuthentication() {
    const accessToken = this.getToken();

    if (!accessToken) {
      return;
    }

    if (!this.isTokenValid(accessToken)) {
      this.storeSessionData(null);
    }
  }

  /**
   * Retorna todas las keys que van al metodo de almacenamiento de información relacionados
   * con la sesion, todo key que se agregue al metodo de almacenamiento de información debe ser
   * agregado a este array para que sea llamado por el metodo @see #logout
   */
  getSessionKeys = () => [TOKEN_KEY, SESSION_INFO_KEY];


  /**
   * Metodo que se encarga de eliminar del metodo de almacenamiento de información
   * todas las keys y values relacionados con la sesión.
   */
  logout = () => {
    this.getSessionKeys().forEach((key) => Cookie.remove(key));
  }

  /**
   * Metodo que contiene la logica necesaria para guardar la informacion de la sesion,
   * despúes de que el usuario se ha autenticado exitosamente o renovado dicha
   * autenticación.
   *
   * @param sessionData los datos que retorna el proveedor de autenticación
   */
  storeSessionData = (sessionData) => {
    if (sessionData) {
      Cookie.set(TOKEN_KEY, sessionData.infosession.access_token);
      Cookie.set(SESSION_INFO_KEY, JSON.stringify(sessionData.infosession));
    } else {
      Cookie.remove(TOKEN_KEY);
      Cookie.remove(SESSION_INFO_KEY);
    }
  };

  /**
   * Metodo que contiene la logica necesaria para obtener el token de sesion
   * con el prefijo indicado en la constante @see BEARER_PREFIX
   *
   * @return string el token de sesion con el prefijo.
   */
  getAuthorizationBearer = () => `${BEARER_PREFIX} ${this.getToken()}`;

  /**
   * Metodo que contiene la logica necesaria para obtener el token de sesion
   * directamente desde el metodo de almacenamiento de información
   *
   * @return string el token de sesion o null si no existe
   */
  getToken = () => Cookie.get(TOKEN_KEY);

  /**
   * Metodo que contiene la logica necesaria para retornar los datos que
   * que entrega el proveedor de autenticacion desde el metodo de almacenamiento de información
   *
   * @return Object o null si no existe dicha key en el metodo de almacenamiento de información
   */
  getSessionInfo = () => {
    const info = Cookie.get(SESSION_INFO_KEY);
    return info ? JSON.parse(info) : null;
  };

  /**
   * Método que contiene la lógica para verificar el estado del token
   * en el lado del cliente, no se debe confiar 100% en este metodo,
   * ya que quien tiene la ultima palabra es el servidor.
   *
   * @return { boolean } si el token está presente y su tiempo de expiracion no ha sido alcanzado
   * y no se está utilizando antes de tiempo
   *
   */
  isTokenValid = async () => {
    const { access_token: token, expires_at: expiration } = await this.getSessionInfo();
    if (token) {
      try {
        const now = Date.now().valueOf() / 1000;
        const expires = Date.parse(expiration);
        const expired = expires < now;
        return !(expired);
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  isAuthenticated = () => !!this.getToken()
}

export default new AuthService();
